import { FIELD_VALUE_TYPE } from '@constants'
import { AutocompleteOption, FormRow, ObjectDataRecord, ObjectDataValue } from '@types'

/**
 * Проверяет является ли поле обогащенным (field.objectValue)
 */
const isEnrichedField = (fieldCode: string, enrichedFieldsSet: Set<string>): boolean => {
  return enrichedFieldsSet.has(fieldCode)
}

/**
 * Проверяет является ли поле ссылочным (требует сохранения label)
 */
const isLinkField = (field?: FormRow): boolean => {
  return Boolean(
    field &&
      (field.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ||
        field.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK)
  )
}

/**
 * Проверяет является ли значение объектом с id и label (AutocompleteOption)
 */
const isAutocompleteOption = (value: unknown): value is AutocompleteOption => {
  return typeof value === 'object' && value !== null && 'id' in value && 'label' in value
}

/**
 * Обновляет значение поля-ссылки, сохраняя label
 */
const updateLinkField = (
  currentValue: ObjectDataValue,
  putValue: ObjectDataValue
): ObjectDataValue => {
  if (isAutocompleteOption(currentValue)) {
    return { ...currentValue, id: putValue as string | number }
  }

  return putValue
}

/**
 * Создает Set обогащенных полей на основе конфигурации формы
 */
const buildEnrichedFieldsSet = (fields: FormRow[]): Set<string> => {
  const enrichedFields = new Set<string>()

  fields.forEach(field => {
    if (field.objectValue && isLinkField(field)) {
      enrichedFields.add(`${field.code}.${field.objectValue}`)
    }
  })

  return enrichedFields
}

/**
 * Объединяет текущие данные формы с данными из PUT ответа.
 *
 * Для полей типа OBJ_INTERNAL_ID_LINK и OBJ_PK_LINK сохраняет структуру { id, label },
 * чтобы не потерять обогащенные данные (label значения).
 *
 * @param currentFormData - текущие данные формы (с обогащенными полями)
 * @param putResponseData - данные из ответа PUT запроса
 * @param fields - конфигурация полей формы
 * @returns объединенные данные для сброса формы
 */
export const mergeFormDataAfterUpdate = (
  currentFormData: ObjectDataRecord,
  putResponseData: ObjectDataRecord,
  fields: FormRow[]
): ObjectDataRecord => {
  const enrichedFieldsSet = buildEnrichedFieldsSet(fields)
  const fieldsMap = new Map(fields.map(f => [f.code, f]))

  // Копируем все текущие данные формы
  const mergedData: ObjectDataRecord = { ...currentFormData }

  // Обновляем поля из PUT ответа
  Object.entries(putResponseData).forEach(([fieldCode, putValue]) => {
    // Пропускаем обогащенные поля - они должны остаться из currentFormData
    if (isEnrichedField(fieldCode, enrichedFieldsSet)) {
      return
    }

    const field = fieldsMap.get(fieldCode)

    // Для полей-ссылок сохраняем label
    if (isLinkField(field)) {
      mergedData[fieldCode] = updateLinkField(currentFormData[fieldCode], putValue)
    } else {
      // Для обычных полей просто обновляем значение
      mergedData[fieldCode] = putValue
    }
  })

  return mergedData
}
